import { useTelemetry, SendEventProps } from "@hooks/useTelemetry";

type Status = "attempt" | "success" | "failure";
export type TriggeredBy = "user" | "system";

export const useAuthTelemetry = () => {
  const { sendEvent } = useTelemetry();

  const sendRegisterEvent = (
    status: Status,
    context?: SendEventProps["context"],
  ): void => {
    if (context?.status) {
      throw new Error("status is not allowed to be provided in the context");
    }

    sendEvent("auth-register", {
      context: {
        status,
        ...context,
      },
    });
  };

  const sendLoginEvent = (
    type: "email_password" | "oauth",
    status: Status,
    context?: SendEventProps["context"],
  ): void => {
    if (context?.type || context?.status) {
      throw new Error(
        "type and status are not allowed to be provided in the context",
      );
    }

    sendEvent("auth-login", {
      context: {
        type,
        status,
        ...context,
      },
    });
  };

  const sendLogoutEvent = (
    status: Status,
    triggeredBy: TriggeredBy,
    context?: SendEventProps["context"],
  ): void => {
    if (context?.status || context?.triggeredBy) {
      throw new Error(
        "status and triggeredBy are not allowed to be provided in the context",
      );
    }

    sendEvent("auth-logout", {
      context: {
        status,
        triggeredBy,
        ...context,
      },
    });
  };

  const sendResetPasswordEvent = (
    status: Status,
    context?: SendEventProps["context"],
  ): void => {
    if (context?.status) {
      throw new Error("status is not allowed to be provided in the context");
    }

    sendEvent("auth-reset-password", {
      context: {
        status,
        ...context,
      },
    });
  };

  const sendModalEvent = (
    action: "open" | "close",
    context?: SendEventProps["context"],
  ): void => {
    if (context?.action) {
      throw new Error("action is not allowed to be provided in the context");
    }

    sendEvent("auth-modal-toggle", {
      context: {
        action,
        ...context,
      },
    });
  };

  return {
    sendRegisterEvent,
    sendLoginEvent,
    sendLogoutEvent,
    sendResetPasswordEvent,
    sendModalEvent,
  };
};
